import React, { ReactNode, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, Navigate } from 'react-router-dom';

import { Loader } from '@/shared/components/core-ui/loader/loader.component';
import { Unauthorized } from '@/shared/components/core-ui/unauthorized/unauthorized.component';
import { TARGET_URL_QUERY_PARAM } from '@/shared/config/auth.config';
import { APP_ROUTES, PUBLIC_PATH } from '@/shared/config/routes-config';
import { UserRole } from '@/shared/enums/user-role.enum';
import { UserTeams } from '@/shared/enums/user-teams.enum';
import { RouteConfig } from '@/shared/interfaces/routes-config.interface';
import { RootState } from '@/shared/redux/reducers';
import { requiresTeamMembershipForRoutes } from '@/shared/services/helpers/auth-helper/auth-helper.service';
/**
 * interface ProtectedRouteProps
 */

interface ProtectedRouteProps {
  /**
   * Rendered as children rather than taken as a `component` prop on purpose.
   *
   * With `component`, every caller built the element type inline — a new function
   * identity on each render — so React treated each render as a different component
   * type and remounted the whole page subtree. That reran every route's mount effects,
   * which is why pages fetched their data twice. Children are plain elements and
   * reconcile by their own stable types, so the footgun is gone rather than worked
   * around with memoisation at each call site.
   */
  children?: ReactNode;
  permittedList?: UserRole[];
  permittedTeamsList?: UserTeams[];
}

/**
 * functional component ProtectedRoute
 * @param children subtree to render once the user is authenticated and permitted
 * @param isAuthenticated user authentication status
 */

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  permittedList,
  permittedTeamsList
}) => {
  const {
    accessToken: isAuthenticated,
    role: userRole,
    team: userTeam,
    userLoaded
  } = useSelector((state: RootState) => state.auth);
  const location = useLocation();

  const TARGET_URL = useMemo(() => {
    return PUBLIC_PATH ? location.pathname.replace(PUBLIC_PATH, '') : location.pathname;
  }, [location.pathname]);

  const userHasPermittedRole = useMemo(() => {
    return permittedList && userRole && permittedList.some((x) => userRole.includes(x));
  }, [permittedList, userRole]);

  const userInPermittedTeam = useMemo(() => {
    return !requiresTeamMembershipForRoutes()
      ? userHasPermittedRole
      : permittedTeamsList
      ? userTeam &&
        userTeam.length > 0 &&
        permittedTeamsList.some((x) => userTeam.some((t) => t.name === x))
      : true;
  }, [permittedTeamsList, userTeam, userHasPermittedRole]);

  if (!isAuthenticated) {
    return (
      <Navigate
        to={{
          pathname: (APP_ROUTES.AUTH_MODULE.CHILDREN as RouteConfig).LOGIN.FULL_PATH,
          search: `?${TARGET_URL_QUERY_PARAM}=${TARGET_URL}`
        }}
      />
    );
  }

  // Auth data (role/team) is still hydrating into Redux — defer the verdict instead of
  // treating a not-yet-loaded profile as a denial, which would flash the Unauthorized
  // screen on navigation until the data lands.
  if (!userLoaded) {
    return <Loader />;
  }

  if (!userHasPermittedRole || !userInPermittedTeam) {
    return <Unauthorized />;
  }

  return <>{children}</>;
};
